import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import FeedCard from '../../../../shared/components/FeedCard';
import { Spinner } from '../../../../core/design-system/components/Spinner';
import './styles.css';

const Feed = ({ 
  posts, 
  loading, 
  hasMore, 
  onLoadMore, 
  onDelete,
  onShare,
  onToggleParticipation,
  isEmpty,
  emptyStateText,
  onActivityClick // Callback optionnel pour mode modal
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef(null);
  const lastPostRef = useRef(null);

  useEffect(() => {
    // Configuration de l'IntersectionObserver pour le chargement infini
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
        setLoadingMore(true);
        onLoadMore().finally(() => setLoadingMore(false));
      }
    }, options);

    // Observer le dernier élément
    if (lastPostRef.current) {
      observerRef.current.observe(lastPostRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore, onLoadMore, posts.length]);

  const handlePostClick = (postId) => {
    navigate(`/dashboard/activities/${postId}`);
  };

  if (loading && posts.length === 0) {
    return (
      <div className="feed-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isEmpty || (!loading && posts.length === 0)) {
    return (
      <div className="feed-empty">
        <p className="feed-empty-text">
          {emptyStateText || t('common:empty_state.no_posts')}
        </p>
      </div>
    );
  }

  return (
    <div className="feed">
      {posts.map((post, index) => (
        <div
          key={post.id}
          ref={index === posts.length - 1 ? lastPostRef : null}
          className="feed-item-container"
        >
          <FeedCard
            post={post}
            onClick={() => handlePostClick(post.id)}
            onActivityClick={onActivityClick}
          />
        </div>
      ))}

      {(loadingMore || (loading && posts.length > 0)) && (
        <div className="feed-loading-more">
          <Spinner size="md" />
        </div>
      )}
    </div>
  );
};

export default Feed; 